/**
 * Shopify OAuth Credentials
 *
 * Reads first-party Shopify app credentials from the `system_settings` table.
 * Client secrets are stored encrypted via lib/encryption.ts (AES-256-GCM).
 *
 * ONE first-party PUBLIC app (the "Shopify OAuth" settings block). Tenants connect
 * their store through it; its install powers BOTH App-Pricing billing (Partner API)
 * and catalog/attribution/orders. Its handle builds the hosted plan-selection URL.
 * The only other kind is a tenant "custom" app (bring-your-own creds, Stripe/manual
 * billing) — handled via per-integration custom_app_* columns, not here.
 */

import type { SupabaseClient } from "@/lib/supabase"
import { decrypt } from "@/lib/encryption"
import { parseShopDomainList } from "@/lib/shopify/shop-domain"

export interface ShopifyOAuthCredentials {
  clientId: string
  clientSecret: string
  scopes: string
}

type KVSetting = { key: string; value: string | null }

// `read_legal_policies` + `read_locales` back the shop-profile read: policy text the agent
// answers customers from, and the store's published languages.
export const DEFAULT_SCOPES =
  "read_orders,read_all_orders,read_customers,read_customer_events,read_marketing_events,read_products,read_inventory,read_legal_policies,read_locales"

/** system_settings keys for the single first-party public app ("Shopify OAuth" block). */
export const SHOPIFY_APP_SETTING_KEYS = {
  clientId: "shopify_app_client_id",
  clientSecret: "shopify_app_client_secret",
  scopes: "shopify_app_scopes",
  handle: "shopify_app_handle",
  signInEnabled: "shopify_sign_in_enabled",
  testShops: "shopify_test_shops",
} as const

/** Global rollout flags of Shopify features; each also turns on for the test stores alone. */
type ShopifyFeatureFlag = typeof SHOPIFY_APP_SETTING_KEYS.signInEnabled

async function readSettings(
  supabase: SupabaseClient,
  keys: string[],
): Promise<Map<string, string>> {
  const { data, error } = await supabase
    .from("system_settings")
    .select("key,value")
    .in("key", keys)

  if (error) {
    throw new Error(`Failed to fetch Shopify credentials: ${error.message}`)
  }

  return new Map(
    ((data ?? []) as KVSetting[]).map((row) => [row?.key, row?.value ?? ""]),
  )
}

/**
 * Get the "system" first-party app credentials from system_settings.
 * Scopes fall back to DEFAULT_SCOPES when unset (this app is the legacy default and
 * may legitimately request the approval-gated `read_all_orders`).
 */
export async function getShopifyCredentials(
  supabase: SupabaseClient,
): Promise<ShopifyOAuthCredentials> {
  const map = await readSettings(supabase, [
    SHOPIFY_APP_SETTING_KEYS.clientId,
    SHOPIFY_APP_SETTING_KEYS.clientSecret,
    SHOPIFY_APP_SETTING_KEYS.scopes,
  ])

  const clientId = map.get(SHOPIFY_APP_SETTING_KEYS.clientId) || ""
  const clientSecretEnc = map.get(SHOPIFY_APP_SETTING_KEYS.clientSecret) || ""
  const scopes = map.get(SHOPIFY_APP_SETTING_KEYS.scopes) || DEFAULT_SCOPES

  if (!clientId || !clientSecretEnc) {
    throw new Error(
      "Shopify OAuth credentials are incomplete in system_settings. Configure Client ID and Client Secret in System Settings.",
    )
  }

  const clientSecret = decrypt(clientSecretEnc)

  if (!clientSecret) {
    throw new Error(
      "Shopify App Client Secret is empty after decryption. Re-enter the secret in System Settings.",
    )
  }

  return { clientId, clientSecret, scopes }
}

/**
 * Read the first-party public app handle used to build the Shopify-hosted plan
 * selection URL (`.../charges/<handle>/pricing_plans`). Fails loud when unset.
 */
export async function getShopifyAppHandle(
  supabase: SupabaseClient,
): Promise<string> {
  const map = await readSettings(supabase, [SHOPIFY_APP_SETTING_KEYS.handle])
  const handle = map.get(SHOPIFY_APP_SETTING_KEYS.handle) || ""
  if (!handle) {
    throw new Error(
      "Shopify app handle is not configured in system_settings (shopify_app_handle). It is required to build the hosted plan-selection URL.",
    )
  }
  return handle
}

/**
 * The store-agnostic admin link that opens the app: Shopify signs the merchant in and picks the store, so the sign-in
 * page never asks for a shop domain (App Store 2.3.1). Only while Shopify sign-in is on for every shop, because the
 * test stores cannot be checked before a shop is known.
 */
export async function getShopifySignInAppUrl(supabase: SupabaseClient): Promise<string | null> {
  try {
    const map = await readSettings(supabase, [SHOPIFY_APP_SETTING_KEYS.signInEnabled, SHOPIFY_APP_SETTING_KEYS.handle])
    const handle = map.get(SHOPIFY_APP_SETTING_KEYS.handle)
    if (map.get(SHOPIFY_APP_SETTING_KEYS.signInEnabled) !== "true" || !handle) return null
    return `https://admin.shopify.com/apps/${encodeURIComponent(handle)}`
  } catch (error) {
    console.warn("[Shopify] Could not read the sign-in link settings", error)
    return null
  }
}

/** Kill switch for every Shopify sign-in behaviour. */
export function isShopifySignInEnabled(supabase: SupabaseClient, shopDomain: string): Promise<boolean> {
  return isShopifyFeatureEnabled(supabase, SHOPIFY_APP_SETTING_KEYS.signInEnabled, shopDomain)
}

/** On for every shop when the global flag is "true", otherwise only for the test stores; unset or unreadable means off. */
export async function isShopifyFeatureEnabled(
  supabase: SupabaseClient,
  flag: ShopifyFeatureFlag,
  shopDomain: string,
): Promise<boolean> {
  try {
    const map = await readSettings(supabase, [flag, SHOPIFY_APP_SETTING_KEYS.testShops])
    if (map.get(flag) === "true") return true
    return parseShopDomainList(map.get(SHOPIFY_APP_SETTING_KEYS.testShops) ?? "").shops.includes(shopDomain.toLowerCase())
  } catch (error) {
    console.warn("[Shopify] Could not read the switch; treating it as off", flag, error)
    return false
  }
}
